/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { type DragEvent, Fragment } from 'react';

import { COLORS } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import type { ScanPhotoTimeline } from '@kaiizen/kai-scan-template-system';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { ProgressAltCell } from './ProgressAltCell';
import { ProgressAltColumnDropZone } from './ProgressAltColumnDropZone';
import { useProgressAlt } from './progressAltContext';
import { ProgressAltDateColumn } from './ProgressAltDateColumn';
import { ProgressAltHeader } from './ProgressAltHeader';
import {
  CANVAS_PADDING_X_PX,
  COLLAPSED_ROW_HEIGHT_PX,
  GAP_PX,
  getColumnWidth,
  getDateColumnWidth,
  getGridLeft,
  getHeaderTitleMaxWidth,
  getRunningOffsets,
  HEADER_HEIGHT_PX,
  type ProgressAltTileSize,
  SCROLL_BAR_HEIGHT_PX,
} from './progressAltMetrics';
import { getColumnKey, getMaxPhotoCount, type ProgressAltColumn } from './progressAltModel';
import { ProgressAltScrollBar } from './ProgressAltScrollBar';
import {
  useHorizontalWheelScrolling,
  useRememberedScrolling,
  useResizeAnchoredScrolling,
  useRevealedCellScrolling,
  useScrollTarget,
} from './progressAltScrollLib';
import { useImageDragInProgress } from './useImageDragInProgress';

const FRAME_STYLE = css`
  label: PROGRESS_ALT_FRAME_STYLE;
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/**
 * Vertical overflow scrolls natively, bar and all. Horizontal overflow is hidden so the browser
 * draws no bar for it — the one along the bottom stands in — which still leaves the element
 * programmatically scrollable for sticky positioning and `scrollLeft`. See
 * `useHorizontalWheelScrolling` for the wheel and trackpad gestures that hiding it costs.
 */
const SCROLL_STYLE = css`
  label: PROGRESS_ALT_SCROLL_STYLE;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

/**
 * No padding on the top or left edges: the sticky header row and sticky date column sit flush against
 * the scrollport, leaving no transparent margin for content to show through.
 *
 * Plain paper behind the photos, no graph ruling. `CANVAS_BACKGROUND` rather than `COLORS.appBg`
 * because it is the mockup's `--app-bg` exactly — the token maps to kai-shiki's nearest neighbour,
 * `surfaceMuted` (#FAF8F2), which reads a shade grey under a wall of photographs.
 */
const CANVAS_BACKGROUND = '#FFFDF8';

const CANVAS_STYLE = css`
  label: PROGRESS_ALT_CANVAS_STYLE;
  display: flex;
  align-items: stretch;
  gap: ${GAP_PX}px;
  padding: 0 ${CANVAS_PADDING_X_PX}px 0 0;
  width: max-content;
  min-width: 100%;
  min-height: 100%;
  background: ${CANVAS_BACKGROUND};
`;

/** Positioned, so each column's drop zone can be laid over its column by pixel offset. */
const GRID_STYLE = css`
  label: PROGRESS_ALT_GRID_STYLE;
  position: relative;
  display: grid;
  align-content: start;
  gap: ${GAP_PX}px;
`;

const SCROLL_BAR_ROW_STYLE = css`
  label: PROGRESS_ALT_SCROLL_BAR_ROW_STYLE;
  flex: 0 0 ${SCROLL_BAR_HEIGHT_PX}px;
  display: flex;
  align-items: stretch;
  border-top: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

/**
 * Carries the date column's tone to the very bottom, so the scrollbar spans only what scrolls.
 * Border-box so its divider lands on exactly the same pixel as the date column's, directly above.
 */
const SCROLL_BAR_GUTTER_STYLE = css`
  box-sizing: border-box;
  border-right: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

const EMPTY_STATE_STYLE = css`
  label: PROGRESS_ALT_EMPTY_STATE_STYLE;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${pxToRem(14)};
  color: ${COLORS.midtone};
`;

type ProgressAltColumnLayout = {
  column: ProgressAltColumn;
  expanded: boolean;
  canExpand: boolean;
  width: number;
};

const getColumnLayouts = (
  timeline: ScanPhotoTimeline,
  columns: readonly ProgressAltColumn[],
  expandedColumnKeys: ReadonlySet<string>,
  tileSize: ProgressAltTileSize,
): ProgressAltColumnLayout[] =>
  columns.map((column, index) => {
    const maxPhotoCount = getMaxPhotoCount(timeline, index);
    const expanded = expandedColumnKeys.has(getColumnKey(column)) && maxPhotoCount > 1;
    return {
      column,
      expanded,
      canExpand: maxPhotoCount > 1,
      width: getColumnWidth(expanded, maxPhotoCount, tileSize),
    };
  });

/**
 * A drop that misses every column lands on the grid itself, where the browser would otherwise open
 * the file in place of the app. Refused outright instead, and the cursor says so before the drop.
 */
const refuseDrop = (event: DragEvent) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'none';
};

const ProgressAltEmptyState = ({ message }: { message: string }) => (
  <main css={EMPTY_STATE_STYLE} aria-label='Progress Alt'>
    <div>{message}</div>
  </main>
);

/**
 * The Progress Alt matrix: one row per photo-bearing time bucket, one column per group and task on the
 * scope. Scrolling is the browser's own on both axes; the bar along the bottom just drives
 * and mirrors the horizontal half of it.
 */
export const ProgressAltGrid = () => {
  const {
    timeline,
    columns,
    tileSize,
    dateColumnCollapsed,
    expandedColumnKeys,
    toggleExpandedColumn,
    getRowNumber,
    collapsedRows,
    toggleCollapsedRow,
    openPhoto,
    revealTarget,
    gridScroll,
    selectedGroupId,
    selectedTaskId,
    scanIsEditable,
  } = useProgressAlt();
  const imageDragInProgress = useImageDragInProgress();
  // The empty states below replace the scroll container while the grid itself stays mounted, so the
  // target carries a token alongside its ref for the effects to notice the swap. See `useScrollTarget`.
  const scrollTarget = useScrollTarget();
  useHorizontalWheelScrolling(scrollTarget);
  // Laid out ahead of the empty states so every hook below runs on every render, empty or not.
  const dateColumnWidth = getDateColumnWidth(dateColumnCollapsed);
  const gridLeft = getGridLeft(dateColumnWidth);
  const columnLayouts = timeline ? getColumnLayouts(timeline, columns, expandedColumnKeys, tileSize) : [];
  // Where each column's left edge falls on the canvas — what the view holds on to across a resize.
  const columnOffsetsPx = getRunningOffsets(columnLayouts.map(layout => layout.width), gridLeft);
  const rowHeights = (timeline?.rows ?? []).map(row => collapsedRows.has(getRowNumber(row)) ? COLLAPSED_ROW_HEIGHT_PX : tileSize.tileHeightPx);
  useResizeAnchoredScrolling(
    scrollTarget,
    { columnOffsetsPx, stickyInsetPx: dateColumnWidth },
    `${tileSize.columnWidthPx}:${dateColumnWidth}`,
  );
  // Declared before the reveal, which measures "already on screen" from the position this restores.
  useRememberedScrolling(scrollTarget, gridScroll);
  useRevealedCellScrolling(scrollTarget, revealTarget, {
    columnOffsetsPx,
    columnWidthsPx: columnLayouts.map(layout => layout.width),
    // Past the header row that precedes them all, and the gap under it.
    rowOffsetsPx: getRunningOffsets(rowHeights, HEADER_HEIGHT_PX + GAP_PX),
    rowHeightsPx: rowHeights,
    stickyLeftInsetPx: gridLeft,
    stickyTopInsetPx: HEADER_HEIGHT_PX,
    photoHeightPx: tileSize.tileHeightPx,
  });

  if (!timeline) return null;
  if (!columns.length) return <ProgressAltEmptyState message='Nothing on the scope yet.' />;
  if (!timeline.rows.length) return <ProgressAltEmptyState message='No photos have been taken yet.' />;

  const isColumnSelected = (column: ProgressAltColumn) =>
    column.taskId ? column.taskId === selectedTaskId : column.groupId === selectedGroupId && !selectedTaskId;

  const headerTitleMaxWidth = getHeaderTitleMaxWidth(tileSize);

  return (
    <div css={FRAME_STYLE} onDragOver={refuseDrop} onDrop={refuseDrop}>
      <div css={SCROLL_STYLE} ref={scrollTarget.attach} data-testid='progress-alt-scroll'>
        <div css={CANVAS_STYLE}>
          <ProgressAltDateColumn rows={timeline.rows} widthPx={dateColumnWidth} />

          <div
            css={GRID_STYLE}
            data-testid='progress-alt-grid'
            style={{
              gridTemplateColumns: columnLayouts.map(layout => `${layout.width}px`).join(' '),
              gridTemplateRows: [HEADER_HEIGHT_PX, ...rowHeights].map(height => `${height}px`).join(' '),
            }}
          >
            {columnLayouts.map(({ column, expanded, canExpand }) => (
              <ProgressAltHeader
                key={`header:${getColumnKey(column)}`}
                column={column}
                selected={isColumnSelected(column)}
                expanded={expanded}
                canExpand={canExpand}
                gridLeftPx={gridLeft}
                titleMaxWidthPx={headerTitleMaxWidth}
              />
            ))}

            {timeline.rows.map((row, rowIndex) => {
              const rowNumber = getRowNumber(row);
              const collapsed = collapsedRows.has(rowNumber);
              return (
                <Fragment key={`row:${rowNumber}`}>
                  {row.cells.map((cell, columnIndex) => {
                    const layout = columnLayouts[columnIndex];
                    return (
                      <ProgressAltCell
                        key={`cell:${rowNumber}:${getColumnKey(cell)}`}
                        column={layout.column}
                        photos={cell.photos}
                        expanded={layout.expanded}
                        collapsed={collapsed}
                        onExpandRow={() => toggleCollapsedRow(rowNumber)}
                        onExpandColumn={() => toggleExpandedColumn(getColumnKey(cell))}
                        onOpenPhoto={photoId => openPhoto({ rowIndex, columnIndex, photoId })}
                      />
                    );
                  })}
                </Fragment>
              );
            })}

            {scanIsEditable && imageDragInProgress
              ? columnLayouts.map(({ column, width }, index) => (
                <ProgressAltColumnDropZone
                  key={`drop:${getColumnKey(column)}`}
                  column={column}
                  leftPx={columnOffsetsPx[index] - gridLeft}
                  widthPx={width}
                />
              ))
              : null}
          </div>
        </div>
      </div>

      <div css={SCROLL_BAR_ROW_STYLE}>
        <div css={SCROLL_BAR_GUTTER_STYLE} style={{ flex: `0 0 ${dateColumnWidth}px` }} />
        <ProgressAltScrollBar
          scrollTarget={scrollTarget}
          insetLeftPx={GAP_PX}
          insetRightPx={CANVAS_PADDING_X_PX}
        />
      </div>
    </div>
  );
};
